import React, { useCallback, useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { ArrowLeft, Save } from 'lucide-react'
import toast from 'react-hot-toast'

import { showSuccess } from '@/lib/swal'
import { PageHeader } from '@/components/shared/PageHeader'
import { EmptyState } from '@/components/shared/EmptyState'
import { Card, CardContent } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Textarea } from '@/components/ui/textarea'
import { Switch } from '@/components/ui/switch'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { useAuthStore } from '@/store/auth.store'
import { roleService, type RoleVm } from '@/services/roleService'
import {
  permissionsService,
  type PermissionLookupItem,
  type PermissionRecord,
  type MyPermissionVm,
} from '@/services/permissionsService'

type TabKey = 'info' | 'permissions' | 'users'

type PermissionRow = {
  /** 0 = new row; >0 when API returned existing permission line */
  lineId: number
  moduleId: number
  menuId: number
  menuName: string
  canRead: boolean
  canUpdate: boolean
  canDelete: boolean
  canInsert: boolean
}

function resolveMenuId(item: PermissionLookupItem, idx: number): number {
  return item.id > 0 ? item.id : item.lookupCodeId > 0 ? item.lookupCodeId : idx + 1
}

/** Merge GetMenu rows with GET GetPermissions — `PermissionRecord.id` becomes `lineId` for save/update. */
function mergeMenusWithSavedPermissions(
  menus: PermissionLookupItem[],
  saved: PermissionRecord[],
  moduleId: number,
  menuLabel: (item: PermissionLookupItem) => string
): PermissionRow[] {
  const bestByMenuId = new Map<number, PermissionRecord>()
  for (const s of saved) {
    const mid = s.menuId
    const prev = bestByMenuId.get(mid)
    if (!prev || s.id > prev.id) {
      bestByMenuId.set(mid, s)
    }
  }

  return menus.map((item, idx) => {
    const menuId = resolveMenuId(item, idx)
    const rec = bestByMenuId.get(menuId)
    return {
      lineId: rec?.id ?? 0,
      moduleId,
      menuId,
      menuName: menuLabel(item),
      canRead: rec ? rec.canRead === 1 : (item.canRead ?? false),
      canUpdate: rec ? rec.canUpdate === 1 : (item.canUpdate ?? false),
      canDelete: rec ? rec.canDelete === 1 : (item.canDelete ?? false),
      canInsert: rec ? rec.canInsert === 1 : (item.canInsert ?? false),
    }
  })
}

const MOCK_ASSIGNED_USERS = [
  { fullName: '', email: '', mobileNumber: '', roleName: '', department: '' },
]

function formatPermissionSaveError(
  error: unknown,
  t: (key: string, opt?: Record<string, unknown>) => string
): string {
  const err = error as {
    response?: { data?: unknown; status?: number }
    message?: string
  }
  const d = err?.response?.data
  const status = err?.response?.status
  if (d && typeof d === 'object') {
    const o = d as Record<string, unknown>
    const msg =
      o.message ??
      o.Message ??
      o.title ??
      o.Title ??
      o.error ??
      o.detail
    if (typeof msg === 'string' && msg.trim()) return msg.trim()
    if (o.errors && typeof o.errors === 'object') {
      try {
        return JSON.stringify(o.errors)
      } catch {
        /* ignore */
      }
    }
  }
  if (typeof d === 'string' && d.trim()) return d.trim()
  if (err?.message && typeof err.message === 'string') return err.message
  return status ? t('rolePage.permissionSaveFailedHttp', { status }) : t('rolePage.permissionSaveFailed')
}

function extractCreatedId(payload: unknown): number | undefined {
  if (payload == null || typeof payload !== 'object') return undefined
  const p = payload as Record<string, unknown>
  const inner = (p.data ?? p.Data ?? p) as Record<string, unknown>
  const rid = inner.id ?? inner.Id ?? p.id ?? p.Id
  if (typeof rid === 'number' && rid > 0) return rid
  if (typeof rid === 'string' && /^\d+$/.test(rid)) return Number(rid)
  return undefined
}

export const RoleDetail: React.FC = () => {
  const { t, i18n } = useTranslation('common')
  const navigate = useNavigate()
  const params = useParams<{ id?: string }>()
  const { user } = useAuthStore()

  const lookupDisplayName = useCallback(
    (item: PermissionLookupItem) => {
      const ar = i18n.language?.toLowerCase().startsWith('ar')
      const name = ar
        ? (item.lookupNameAr || item.lookupNameEn || '').trim()
        : (item.lookupNameEn || item.lookupNameAr || '').trim()
      return name || '—'
    },
    [i18n.language]
  )

  const moduleOptionLabel = useCallback(
    (m: PermissionLookupItem) => {
      const lab = lookupDisplayName(m)
      return lab === '—' ? t('rolePage.moduleFallback', { id: m.id }) : lab
    },
    [lookupDisplayName, t]
  )

  const branchId = user?.selectedBranch?.branchId ?? 0
  const rawId = params.id
  const isEditMode = Boolean(rawId && rawId !== 'new')

  const [activeTab, setActiveTab] = useState<TabKey>('info')
  const [roleName, setRoleName] = useState('')
  const [status, setStatus] = useState<'Active' | 'Not Active'>('Not Active')
  const [description, setDescription] = useState('')
  const [permissionModules, setPermissionModules] = useState<PermissionLookupItem[]>([])
  const [modulesLoading, setModulesLoading] = useState(false)
  const [selectedModuleId, setSelectedModuleId] = useState<number | null>(null)
  const [menuLoading, setMenuLoading] = useState(false)
  const [permissions, setPermissions] = useState<PermissionRow[]>([])
  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)
  const [loadedVm, setLoadedVm] = useState<RoleVm | null>(null)

  const secondaryTabsEnabled = isEditMode

  useEffect(() => {
    if (!secondaryTabsEnabled && (activeTab === 'permissions' || activeTab === 'users')) {
      setActiveTab('info')
    }
  }, [secondaryTabsEnabled, activeTab])

  useEffect(() => {
    if (!branchId || !isEditMode || !rawId) return
    const idNum = Number(rawId)
    if (!idNum) return

    const load = async () => {
      try {
        setLoading(true)
        const vm = await roleService.getRoleById(idNum)
        if (!vm) {
          toast.error(t('rolePage.toastRoleNotFound'))
          navigate('/security/roles')
          return
        }
        setLoadedVm(vm)
        setRoleName(vm.roleName || '')
        setDescription(vm.description || '')
        setStatus(vm.isActive === 1 ? 'Active' : 'Not Active')
      } catch (error: any) {
        toast.error(error?.response?.data?.message || t('rolePage.toastLoadRoleFailed'))
        navigate('/security/roles')
      } finally {
        setLoading(false)
      }
    }

    load()
  }, [branchId, isEditMode, rawId, navigate])

  useEffect(() => {
    if (!isEditMode) {
      setPermissionModules([])
      setSelectedModuleId(null)
      setPermissions([])
      return
    }

    let cancelled = false
    const loadModules = async () => {
      try {
        setModulesLoading(true)
        const list = await permissionsService.getModules()
        if (!cancelled) setPermissionModules(list)
      } catch (error: any) {
        if (!cancelled) {
          toast.error(error?.response?.data?.message || t('rolePage.toastLoadModulesFailed'))
          setPermissionModules([])
        }
      } finally {
        if (!cancelled) setModulesLoading(false)
      }
    }

    loadModules()
    return () => {
      cancelled = true
    }
  }, [isEditMode])

  useEffect(() => {
    if (!isEditMode || selectedModuleId == null) {
      setPermissions([])
      return
    }

    const mod = permissionModules.find((m) => m.id === selectedModuleId)
    const moduleName = mod?.lookupNameEn?.trim()
    if (!moduleName) {
      setPermissions([])
      return
    }

    const roleIdForMenu =
      isEditMode && rawId ? Number(rawId) : undefined

    let cancelled = false
    const loadMenu = async () => {
      try {
        setMenuLoading(true)
        const rid =
          roleIdForMenu && !Number.isNaN(roleIdForMenu) ? roleIdForMenu : undefined
        const menus = await permissionsService.getMenuByModuleName(moduleName, rid)

        let saved: PermissionRecord[] = []
        if (rid && branchId && selectedModuleId) {
          try {
            saved = await permissionsService.getPermissions(rid, selectedModuleId, branchId)
          } catch (e) {
            console.warn('[Permissions] GetPermissions failed', e)
          }
        }

        if (cancelled) return
        setPermissions(mergeMenusWithSavedPermissions(menus, saved, selectedModuleId, lookupDisplayName))
      } catch (error: any) {
        if (!cancelled) {
          toast.error(error?.response?.data?.message || t('rolePage.toastLoadMenusFailed'))
          setPermissions([])
        }
      } finally {
        if (!cancelled) setMenuLoading(false)
      }
    }

    loadMenu()
    return () => {
      cancelled = true
    }
  }, [isEditMode, selectedModuleId, permissionModules, rawId, branchId, lookupDisplayName])

  const tabClasses = (tab: TabKey) => {
    const enabled = tab === 'info' || secondaryTabsEnabled
    const active = activeTab === tab
    if (!enabled) {
      return 'px-4 py-2 text-sm border-b-2 border-transparent text-muted-foreground opacity-45 cursor-not-allowed'
    }
    return `px-4 py-2 text-sm border-b-2 transition-colors ${
      active ? 'border-primary bg-primary text-primary-foreground' : 'border-transparent text-muted-foreground'
    }`
  }

  const updatePermission = (rowIndex: number, key: keyof PermissionRow, value: boolean) => {
    setPermissions((prev) =>
      prev.map((p, i) => (i === rowIndex ? { ...p, [key]: value } : p))
    )
  }

  const handleTabClick = (tab: TabKey) => {
    if (tab !== 'info' && !secondaryTabsEnabled) return
    setActiveTab(tab)
  }

  const handleSubmit = async () => {
    if (!branchId) {
      toast.error(t('rolePage.toastBranch'))
      return
    }

    if (activeTab === 'permissions') {
      if (!isEditMode || !rawId) {
        toast.error(t('rolePage.toastPermissionsEditOnly'))
        return
      }
      if (selectedModuleId == null) {
        toast.error(t('rolePage.toastSelectModuleFirst'))
        return
      }
      if (permissions.length === 0) {
        toast.error(t('rolePage.toastNoMenuRows'))
        return
      }

      const roleId = loadedVm?.id ?? Number(rawId)
      if (!roleId) {
        toast.error(t('rolePage.toastInvalidRole'))
        return
      }

      const myPermssionsVms: MyPermissionVm[] = permissions.map((row) => ({
        lineId: row.lineId,
        roleId,
        moduleId: row.moduleId,
        menuId: row.menuId,
        canRead: row.canRead ? 1 : 0,
        canUpdate: row.canUpdate ? 1 : 0,
        canDelete: row.canDelete ? 1 : 0,
        canInsert: row.canInsert ? 1 : 0,
        isDeleted: false,
      }))

      // Backend expects the same actionType casing for both new rows (lineId 0) and updates.
      const permissionsSavePayload = {
        actionType: 'Save' as const,
        branchId,
        myPermssionsVms,
      }

      try {
        setSaving(true)
        toast.dismiss()
        await permissionsService.savePermissions(permissionsSavePayload)
        await showSuccess(t('rolePage.permissionsSavedTitle'), t('rolePage.permissionsSavedMessage'))

        const mod = permissionModules.find((m) => m.id === selectedModuleId)
        const moduleName = mod?.lookupNameEn?.trim()
        if (moduleName && selectedModuleId != null && branchId) {
          const menus = await permissionsService.getMenuByModuleName(moduleName, roleId)
          let saved: PermissionRecord[] = []
          try {
            saved = await permissionsService.getPermissions(roleId, selectedModuleId, branchId)
          } catch (e) {
            console.warn('[Permissions] GetPermissions reload failed', e)
          }
          setPermissions(mergeMenusWithSavedPermissions(menus, saved, selectedModuleId, lookupDisplayName))
        }
      } catch (error: any) {
        const apiMsg = formatPermissionSaveError(error, t)
        console.error('[Permissions Save] Failed:', error?.response?.data ?? error)
        toast.error(apiMsg)
      } finally {
        setSaving(false)
      }
      return
    }

    if (activeTab === 'users') {
      toast.error(t('rolePage.toastAssignedUsersNotSaved'))
      return
    }

    if (!roleName.trim()) {
      toast.error(t('rolePage.toastRoleNameRequired'))
      return
    }
    if (!description.trim()) {
      toast.error(t('rolePage.toastDescriptionRequired'))
      return
    }

    const base: RoleVm = loadedVm ?? {
      id: 0,
      roleName: '',
      description: '',
      isActive: 0,
      branchId,
    }

    const vm: RoleVm = {
      ...base,
      id: isEditMode ? base.id : 0,
      roleName: roleName.trim(),
      description: description.trim(),
      isActive: status === 'Active' ? 1 : 0,
      branchId,
    }

    try {
      setSaving(true)
      toast.dismiss()
      if (isEditMode) {
        await roleService.updateRole(vm)
        await showSuccess(t('rolePage.roleUpdatedTitle'), t('rolePage.roleUpdatedMessage'))
        navigate('/security/roles')
      } else {
        const res = await roleService.saveRole(vm)
        await showSuccess(t('rolePage.roleCreatedTitle'), t('rolePage.roleCreatedMessage'))
        const newId = extractCreatedId(res)
        if (newId) {
          navigate(`/security/roles/${newId}`)
        } else {
          navigate('/security/roles')
        }
      }
    } catch (error: any) {
      toast.error(error?.response?.data?.message || t('rolePage.toastSaveRoleFailed'))
    } finally {
      setSaving(false)
    }
  }

  const req = t('rolePage.requiredMark')

  if (!branchId) {
    return (
      <div className="space-y-6 p-6">
        <PageHeader title={t('rolePage.pageTitle')} description={t('rolePage.pageDescription')} />
        <Card>
          <CardContent className="p-6">
            <EmptyState title={t('rolePage.branchRequiredTitle')} description={t('rolePage.branchRequiredDescriptionForm')} />
          </CardContent>
        </Card>
      </div>
    )
  }

  return (
    <div className="space-y-6 p-6">
      <PageHeader
        title={t('rolePage.pageTitle')}
        description={t('rolePage.pageDescription')}
        action={{
          label: saving ? t('rolePage.saving') : t('rolePage.submit'),
          onClick: handleSubmit,
          icon: Save,
          disabled: saving || loading,
        }}
      >
        <Button
          type="button"
          variant="outline"
          disabled={saving}
          onClick={() => navigate('/security/roles')}
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          {t('rolePage.return')}
        </Button>
      </PageHeader>

      <Card>
        <CardContent className="p-6 space-y-6">
          {loading ? (
            <div className="flex items-center justify-center py-16">
              <div className="h-8 w-8 animate-spin rounded-full border-2 border-primary border-t-transparent" />
            </div>
          ) : (
            <>
              <div className="flex border-b flex-wrap">
                <button type="button" className={tabClasses('info')} onClick={() => handleTabClick('info')}>
                  ROLE INFORMATION
                </button>
                <button
                  type="button"
                  className={tabClasses('permissions')}
                  onClick={() => handleTabClick('permissions')}
                  disabled={!secondaryTabsEnabled}
                >
                  ROLE PERMISSIONS
                </button>
                <button
                  type="button"
                  className={tabClasses('users')}
                  onClick={() => handleTabClick('users')}
                  disabled={!secondaryTabsEnabled}
                >
                  ASSIGNED USERS
                </button>
              </div>

              {activeTab === 'info' && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <Label>{req} {t('rolePage.labelRoleName')}</Label>
                    <Input placeholder={t('rolePage.placeholderRoleName')} value={roleName} onChange={(e) => setRoleName(e.target.value)} />
                  </div>
                  <div className="space-y-2">
                    <Label>{req} {t('rolePage.labelStatus')}</Label>
                    <Select value={status} onValueChange={(v) => setStatus(v as 'Active' | 'Not Active')}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Active">{t('rolePage.statusActiveValue')}</SelectItem>
                        <SelectItem value="Not Active">{t('rolePage.statusNotActiveValue')}</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="md:col-span-2 space-y-2">
                    <Label>{req} {t('rolePage.labelDescription')}</Label>
                    <Textarea
                      placeholder={t('rolePage.placeholderDescription')}
                      value={description}
                      onChange={(e) => setDescription(e.target.value)}
                      rows={4}
                    />
                  </div>
                </div>
              )}

              {activeTab === 'permissions' && secondaryTabsEnabled && (
                <div className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className="space-y-2">
                      <Label>{t('rolePage.labelModules')}</Label>
                      <Select
                        value={selectedModuleId != null ? String(selectedModuleId) : undefined}
                        onValueChange={(v) => setSelectedModuleId(v ? Number(v) : null)}
                        disabled={modulesLoading || saving}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder={modulesLoading ? t('rolePage.loadingShort') : t('rolePage.placeholderSelect')} />
                        </SelectTrigger>
                        <SelectContent>
                          {permissionModules.map((m) => (
                            <SelectItem key={m.id} value={String(m.id)}>
                              {moduleOptionLabel(m)}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  <div className="rounded-md border overflow-x-auto min-h-[120px]">
                    {menuLoading ? (
                      <div className="flex items-center justify-center py-12">
                        <div className="h-8 w-8 animate-spin rounded-full border-2 border-primary border-t-transparent" />
                      </div>
                    ) : selectedModuleId == null ? (
                      <p className="py-8 text-center text-sm text-muted-foreground">{t('rolePage.selectModuleForMenus')}</p>
                    ) : permissions.length === 0 ? (
                      <p className="py-8 text-center text-sm text-muted-foreground">{t('rolePage.noMenusForModule')}</p>
                    ) : (
                      <Table>
                        <TableHeader>
                          <TableRow className="border-b-2 border-primary/20 hover:bg-transparent">
                            <TableHead className="h-12 min-w-[180px] px-4 text-left bg-primary/5 font-bold text-primary dark:bg-primary/10 dark:text-primary">
                              {t('rolePage.colMenuName')}
                            </TableHead>
                            <TableHead className="h-12 w-[120px] px-4 text-center bg-primary/5 font-bold text-primary dark:bg-primary/10 dark:text-primary">
                              {t('rolePage.colCanRead')}
                            </TableHead>
                            <TableHead className="h-12 w-[120px] px-4 text-center bg-primary/5 font-bold text-primary dark:bg-primary/10 dark:text-primary">
                              {t('rolePage.colCanInsert')}
                            </TableHead>
                            <TableHead className="h-12 w-[120px] px-4 text-center bg-primary/5 font-bold text-primary dark:bg-primary/10 dark:text-primary">
                              {t('rolePage.colCanUpdate')}
                            </TableHead>
                            <TableHead className="h-12 w-[120px] px-4 text-center bg-primary/5 font-bold text-primary dark:bg-primary/10 dark:text-primary">
                              {t('rolePage.colCanDelete')}
                            </TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {permissions.map((row, rowIndex) => (
                            <TableRow
                              key={`${row.lineId}-${row.menuId}-${rowIndex}`}
                              className="border-b hover:bg-primary/5 dark:hover:bg-primary/10 transition-colors"
                            >
                              <TableCell className="px-4 py-3 text-sm text-left align-middle">
                                {row.menuName}
                              </TableCell>
                              <TableCell className="px-4 py-3 text-center align-middle">
                                <div className="flex justify-center">
                                  <Switch
                                    checked={row.canRead}
                                    onCheckedChange={(v) => updatePermission(rowIndex, 'canRead', v)}
                                  />
                                </div>
                              </TableCell>
                              <TableCell className="px-4 py-3 text-center align-middle">
                                <div className="flex justify-center">
                                  <Switch
                                    checked={row.canInsert}
                                    onCheckedChange={(v) => updatePermission(rowIndex, 'canInsert', v)}
                                  />
                                </div>
                              </TableCell>
                              <TableCell className="px-4 py-3 text-center align-middle">
                                <div className="flex justify-center">
                                  <Switch
                                    checked={row.canUpdate}
                                    onCheckedChange={(v) => updatePermission(rowIndex, 'canUpdate', v)}
                                  />
                                </div>
                              </TableCell>
                              <TableCell className="px-4 py-3 text-center align-middle">
                                <div className="flex justify-center">
                                  <Switch
                                    checked={row.canDelete}
                                    onCheckedChange={(v) => updatePermission(rowIndex, 'canDelete', v)}
                                  />
                                </div>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    )}
                  </div>
                </div>
              )}

              {activeTab === 'users' && secondaryTabsEnabled && (
                <div className="rounded-md border overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>{t('rolePage.colFullName')}</TableHead>
                        <TableHead>{t('rolePage.colEmail')}</TableHead>
                        <TableHead>{t('rolePage.colMobileNumber')}</TableHead>
                        <TableHead>{t('rolePage.colAssignedRoleName')}</TableHead>
                        <TableHead>{t('rolePage.colDepartment')}</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {MOCK_ASSIGNED_USERS.map((row, idx) => (
                        <TableRow key={idx}>
                          <TableCell>{row.fullName || '—'}</TableCell>
                          <TableCell>{row.email || '—'}</TableCell>
                          <TableCell>{row.mobileNumber || '—'}</TableCell>
                          <TableCell>{row.roleName || '—'}</TableCell>
                          <TableCell>{row.department || '—'}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>
    </div>
  )
}
